import formatComma from "react-lib/utils/formatComma";

import { optionText } from "../common/displayTextV4";
import type { ChipV4Tone } from "../common/ui";
import {
  PERFORMERS,
  SUPPLY_USES,
  type DoctorOption,
  type DrugMaster,
  type OrderMaster,
  type OrderMasterOptions,
  type SheetLine,
} from "../common/sequence/OrderSheetV4";
import { IMAGING_SIDES, PREGNANCY, URGENCY_LABELS, supplyTypeOf } from "./ordersModelV4";

// ---------- ช่องกรอกของแต่ละบรรทัด ----------

export type Field = {
  key: string;
  label: string;
  width: number | "full" | "grow";
  required?: boolean;
  placeholder?: string;
  options?: [string, string][];
  /** ช่องค้นหาที่เลือกจากรายการ */
  combo?: boolean;
  /** ตัวเลือกจาก master */
  master?: OrderMaster;
  /** พิมพ์เองได้ มีรายการแนะนำจาก master */
  suggest?: OrderMaster;
  /** บังคับเมื่อยาตั้ง require_<ค่านี้> */
  requiredBy?: DrugMaster;
  numeric?: boolean;
  maxLength?: number;
  /** แสดงเมื่อ */
  when?: (fields: Record<string, string>, raw: SheetLine["raw"]) => boolean;
};

const FIELDS: Record<SheetLine["kind"], Field[]> = {
  imaging: [
    { key: "side", label: "ตำแหน่ง / ข้าง", width: 96, options: IMAGING_SIDES.map((v) => [v, v]) },
    { key: "view", label: "ท่า / Views", width: 150, placeholder: "เช่น AP, Lateral" },
    { key: "urgency", label: "ความเร่งด่วน", width: 100, options: Object.entries(URGENCY_LABELS) },
    { key: "preg", label: "การตั้งครรภ์", width: 130, options: PREGNANCY.map((v) => [v, v]) },
    {
      key: "ind",
      label: "Clinical indication",
      width: "full",
      required: true,
      placeholder: "เหตุผลทางคลินิกสำหรับรังสีแพทย์",
    },
  ],
  treatment: [
    {
      key: "performer",
      label: "ผู้ทำหัตถการ (บทบาท)",
      width: 150,
      required: true,
      placeholder: "ไม่ระบุ",
      options: PERFORMERS,
    },
    {
      key: "icd",
      label: "ICD-9-CM",
      width: 300,
      placeholder: "ค้นหารหัส / ชื่อหัตถการ",
      combo: true,
    },
    { key: "detail", label: "รายละเอียด", width: 240, placeholder: "เช่น หลังส่วนล่าง · ข้างซ้าย" },
    {
      key: "equipment",
      label: "อุปกรณ์ที่ใช้",
      width: "full",
      placeholder: "เช่น เข็ม 0.25×40 mm · ชุดทำแผล",
    },
  ],
  drug: [
    { key: "route", label: "Route", width: 150, master: "route" },
    { key: "site", label: "Site", width: 150, master: "site" },
    {
      key: "dose",
      label: "Dose (ครั้งละ)",
      width: 110,
      required: true,
      requiredBy: "unit",
      numeric: true,
    },
    { key: "unit", label: "Unit", width: 120, master: "unit" },
    { key: "frequency", label: "Frequency", width: 220, required: true, master: "frequency" },
    { key: "prn", label: "PRN (กินเมื่อ…)", width: 240, suggest: "prn" },
    {
      key: "duration",
      label: "จำนวนวัน",
      width: 100,
      required: true,
      numeric: true,
      when: (fields, raw) => fields.home === "Y" && !raw.asNeeded,
    },
    { key: "qty", label: "QTY (พิมพ์เองได้)", width: 130, numeric: true },
    { key: "description", label: "Description", width: "full", maxLength: 60 },
  ],
  supply: [
    {
      key: "use",
      label: "รูปแบบการใช้ (กรณีใช้ที่คลินิก)",
      width: "grow",
      options: SUPPLY_USES,
      when: (fields) => fields.home !== "Y",
    },
    {
      key: "costCenter",
      label: "Cost center ที่รับต้นทุน",
      width: "grow",
      required: true,
      master: "costCenter",
      when: (fields) => fields.home !== "Y" && fields.use === "COST_CENTER",
    },
    { key: "note", label: "หมายเหตุ", width: "full" },
  ],
  note: [
    {
      key: "detail",
      label: "ข้อความคำสั่ง (Free text · ไม่มีค่าใช้จ่าย)",
      width: "full",
      required: true,
      placeholder: "เช่น วัดความดันซ้ำหลังพัก 15 นาที · แจ้งแพทย์ถ้า > 160/100",
    },
    {
      key: "reason",
      label: "เหตุผลที่ดำเนินการแทนแพทย์",
      width: "full",
      required: true,
      when: (fields) => fields.onBehalf === "Y",
    },
  ],
  df: [
    {
      key: "employee",
      label: "แพทย์ผู้รับ DF",
      width: "full",
      required: true,
      placeholder: "ค้นหาชื่อแพทย์",
    },
  ],
  misc: [
    {
      key: "note",
      label: "รายละเอียด",
      width: "full",
      placeholder: "ระบุรายละเอียดของรายการ เช่น ค่าถ่ายเอกสาร 5 ชุด",
    },
  ],
};

// ---------- สรุป / ตรวจบรรทัด ----------

export const visibleFields = (line: SheetLine) =>
  FIELDS[line.kind]
    .filter((field) => !field.when || field.when(line.fields, line.raw))
    .map((field) => {
      const detail = line.raw.detail;
      if (field.requiredBy && detail) {
        return { ...field, required: !!detail[`require_${field.requiredBy}`] };
      }

      return field.master && detail?.[`require_${field.master}`]
        ? { ...field, required: true }
        : field;
    });

const LIMITED_KEYS: Partial<Record<OrderMaster, string>> = {
  unit: "limited_dosage_units",
  route: "limited_routes",
  site: "limited_sites",
  frequency: "limited_frequencies",
};

/** ตัวเลือกใน master (ยาที่จำกัดไว้ = เฉพาะที่จำกัด) */
export const masterOptionsOf = (
  line: SheetLine,
  master: OrderMaster,
  masterOptions?: OrderMasterOptions,
) => {
  const limited: unknown[] = line.raw.detail?.[LIMITED_KEYS[master] ?? ""] ?? [];
  const allowed = new Set([...limited.map(String), line.fields[master]]);

  return (masterOptions?.[master] ?? []).filter(
    (option) => limited.length <= 0 || allowed.has(String(option.value)),
  );
};

const FACT_ROWS: [string, string][] = [
  ["drug_using", "วิธีใช้ยา"],
  ["suggestions", "ข้อแนะนำ"],
  ["cautions", "ข้อควรระวัง"],
  ["warnings", "คำเตือน"],
  ["others", "ข้อมูลอื่นๆ"],
];

export const drugFactText = (line: SheetLine) => {
  const factsheet: Record<string, string> = line.raw.detail?.factsheet_data ?? {};
  const rows = FACT_ROWS.filter(([key]) => factsheet[key]?.trim()).map(
    ([key, label]) => `${label}: ${factsheet[key].trim()}`,
  );

  return rows.length > 0 ? `ข้อมูลยา: ${rows.join(" · ")}` : "ยานี้ไม่มีข้อมูลยาใน Master";
};

export const isQtyMissing = (line: SheetLine) => line.kind === "drug" && line.quantity <= 0;

const isPriceMissing = (line: SheetLine) =>
  line.kind === "misc" && !line.masterPrice && line.priceUnit <= 0;

export const isIcdUnpicked = (line: SheetLine) =>
  line.kind === "treatment" && !!line.fields.icd?.trim() && !line.fields.icd9;

export const sheetMissing = (line: SheetLine): string[] => [
  ...visibleFields(line)
    .filter((field) => field.required && !String(line.fields[field.key] ?? "").trim())
    .map((field) => field.label),
  ...(isIcdUnpicked(line) ? ["ICD-9-CM (เลือกจากรายการ)"] : []),
  ...(isQtyMissing(line) ? ["QTY"] : []),
  ...(isPriceMissing(line) ? ["ราคา/หน่วย"] : []),
];

export const isCostCenter = (line: SheetLine) =>
  line.kind === "supply" && supplyTypeOf(line) === "COST_CENTER";

export const sheetAmount = (line: SheetLine) =>
  isCostCenter(line) ? 0 : line.priceUnit * line.quantity;

const labelOf = (options: [string, string][], value: string) =>
  options.find(([key]) => key === value)?.[1] ?? value;

export const sheetSummary = (
  line: SheetLine,
  doctorOptions: DoctorOption[],
  masterOptions?: OrderMasterOptions,
): string => {
  const fields = line.fields;

  if (line.kind === "imaging") {
    return [
      URGENCY_LABELS[fields.urgency] ?? fields.urgency,
      fields.side === "-" ? "" : fields.side,
      fields.view,
      fields.ind,
    ]
      .filter(Boolean)
      .join(" · ");
  }

  if (line.kind === "treatment") {
    return [
      fields.performer ? labelOf(PERFORMERS, fields.performer) : "",
      fields.icd9 ? fields.icd : "",
      fields.detail,
    ]
      .filter(Boolean)
      .join(" · ");
  }

  if (line.kind === "drug") {
    const label = (master: DrugMaster) => optionText(masterOptions?.[master], fields[master]);

    return [
      fields.priority === "STAT" ? "STAT" : "",
      fields.dose ? `ครั้งละ ${fields.dose} ${label("unit")}`.trim() : "",
      label("route"),
      label("site"),
      label("frequency"),
      fields.prn,
      line.raw.asNeeded || fields.home !== "Y" || !fields.duration ? "" : `${fields.duration} วัน`,
      line.quantity > 0 ? `QTY ${line.quantity}` : "",
      fields.description,
    ]
      .filter(Boolean)
      .join(" · ");
  }

  if (line.kind === "df") {
    const doctor = doctorOptions.find((item) => String(item.employee) === fields.employee);

    return doctor?.name ?? "";
  }

  if (line.kind === "supply") {
    if (fields.home === "Y") {
      return "จ่ายกลับบ้าน · เก็บเงิน";
    }

    if (!isCostCenter(line)) {
      return "ใช้ที่คลินิก · Refill เก็บเงินเพิ่ม";
    }

    const costCenter = optionText(masterOptions?.costCenter, fields.costCenter);
    const cost = formatComma(line.priceUnit * line.quantity, "en-US", false);

    return [
      `ใช้ที่คลินิก · Cost center ${costCenter}`.trim(),
      `ไม่เก็บเงินเพิ่ม (ต้นทุน ${cost} ฿)`,
    ].join(" · ");
  }

  return fields.detail ?? fields.note ?? "";
};

export const sheetMode = (line: SheetLine): [string, ChipV4Tone] | null => {
  if (line.kind !== "supply" && line.kind !== "drug") {
    return null;
  }

  if (line.fields.home === "Y") {
    return ["กลับบ้าน", "sky"];
  }

  if (line.kind === "drug") {
    return ["ใช้ที่คลินิก", "warn"];
  }

  return isCostCenter(line) ? ["Cost center · เหมารวม", "ok"] : ["Refill · เก็บเงิน", "warn"];
};

/* ------------------------------------------------------ */
